import { CountMetric, LatencyMetric, EnvironmentDetail, globalEventBus, MissingArgumentsException, InvalidActionException, UnsupportedEnvironmentException, PairingCancelledException } from '@amzn/ihm-associate-tools-js-lib'
import { SeawardEventBusTopicsConstants } from '@utils/Constants/SeawardEventBusTopicsConstants'
import { generateDeferredPromise } from '@utils/DeferredPomiseUtil'
import { BluetoothPairedDevicesService } from '@services/bluetooth/BluetoothPairedDevicesService'

export class BluetoothModalService {

    public initiateModalDeferred

    // Public functions
    /**
     * Pairs and connects to the device by publishing an event to the BluetoothModalController.
     * This triggers a Bluetooth pair and connect modal to be displayed.
     * @param {object} config Configuration that is necessary for pairing and connection
     * Example
     * {
     *     deviceType: 'HEADSET'
     * }
     */

    public pairAndConnectToDevice (config) {
        const pairAndConnectFaultMetrics = new CountMetric(undefined, 'Fault', 'IhmFlyingDutchman', 'bluetoothModalService.pairAndConnectToDevice')
        const pairAndConnectLatencyMetrics = new LatencyMetric(undefined, 'Time', 'IhmFlyingDutchman', 'bluetoothModalService.pairAndConnectToDevice')
        pairAndConnectLatencyMetrics.start()
        if (!config || !config.deviceType) {
            pairAndConnectFaultMetrics.count(1)
            pairAndConnectLatencyMetrics.stop()
            return Promise.reject(new MissingArgumentsException({
                message: 'Necessary configurations are missing',
            }))
        }
        console.debug('Initiating pair and connect through modal')
        return this.initiatePairAndConnectWithBluetoothModal(config)
            .then((result) => {
                console.info('Modal service pair and connect successfully completed for device with MAC address', result.macAddress)
                pairAndConnectFaultMetrics.count(0)
                return BluetoothPairedDevicesService.setLastPairedDevice(config.deviceType, result.macAddress)
                    .then(() => {
                        console.debug('Last paired bluetooth device successfully updated with device: ' + result.macAddress + ' from service: bluetoothModalService')
                    })
                    .catch(() => {
                        console.info('Last paired bluetooth device not updated.')
                    })
            })
            .catch((e) => {
                console.log('There was an error while pairing and connecting through Bluetooth modal', e)
                pairAndConnectFaultMetrics.count(1)
                return Promise.reject(e)
            })
            .finally(() => {
                pairAndConnectLatencyMetrics.stop()
            })
    }

    // Private functions
    /**
     * Registers a listener with the EventBus to get notified when the BluetoothModalController has a result ready to be processed
     */
    public activate () {
        globalEventBus.on(SeawardEventBusTopicsConstants.BLUETOOTH_PAIRING_MODAL_RESULT, 'bluetoothModalService', this.onBluetoothPairingResult)
    }

    /**
     * Callback function that gets called when the BluetoothModalController is done processing a request.
     * @param {object} data - data coming from the EventBus containing the result of the request.
     */
    private onBluetoothPairingResult (data) {
        if (!this.initiateModalDeferred) {
            console.warn('Got data back when there was no deferred', data)
            return
        }

        const result = data.data

        if (result.success) {
            this.resolveDeferred(result)
            this.initiateModalDeferred = null
            return
        }

        this.rejectDeferred(result)
        this.initiateModalDeferred = null
    }

    /**
     * Verifies the configuration and emits an event so that the BluetoothModalController can start the UI workflow and
     * pair and connect. Note that only one request for pairing can be happening at any given time.
     * @param {object} config - The config object (the deviceType key is required).
     * @return {Promise} resolves if the pairing and connection is successful. Rejects otherwise.
     */

    private initiatePairAndConnectWithBluetoothModal (config) {

        if (this.initiateModalDeferred) {
            return Promise.reject(new InvalidActionException({
                message: 'Bluetooth modal cannot be initiated when it is already processing',
            }))
        }
        if (!EnvironmentDetail.isAndroid()) {
            return Promise.reject(new UnsupportedEnvironmentException({
                message: 'Bluetooth is only supported on Android.',
            }))
        }
        this.initiateModalDeferred = generateDeferredPromise<any>()
        // BluetoothModalService.initiateModalDeferred = new Promise<any>((resolve, reject)=>{})
        globalEventBus.publish(SeawardEventBusTopicsConstants.DISPLAY_BLUETOOTH_PAIRING_MODAL, config)
        return this.initiateModalDeferred.promise
    }

    /**
     * Resolves the current deferred.
     * @param {object} result - result coming from the EventBus.
     */
    private resolveDeferred (result) {
        const macAddress = result.data.macAddress
        this.initiateModalDeferred.resolve({
            macAddress,
        })
    }

    /**
     * Rejects the current deferred.
     * @param {object} result - result coming from the EventBus.
     */
    private rejectDeferred (result) {
        this.initiateModalDeferred.reject(new PairingCancelledException({
            message: result.error || 'Cancelled',
        }))
    }
}
